/**
 * "Ligger du rätt i tid?" (slice D, P2-4): topics practised against the topics the lectures have
 * taken up so far (the lecture of the day included). Only course-map topics count on both sides,
 * so the line matches the map. Hidden before the first lecture and without a course catalogue.
 * The weekly goal strip of P2-4 is deferred (SLICE_D_report.md).
 */
import { Link } from "react-router";
import { Card } from "@client/components/ui";
import { useLectureTimeline } from "@client/features/content/course";
import { useT } from "@client/i18n";
import { lastTaught, taughtTopics } from "@shared/courseCalendar";
import type { ChapterProgress } from "./types";

export function paceOf(
  chapters: readonly Pick<ChapterProgress, "topics">[],
  taught: ReadonlySet<string>,
): { practised: number; taught: number } {
  const inMap = chapters
    .flatMap((c) => c.topics)
    .filter((t) => t.eligible && taught.has(t.topicId));
  return { practised: inMap.filter((t) => t.items > 0).length, taught: inMap.length };
}

export function PaceCard({ chapters }: { chapters: readonly Pick<ChapterProgress, "topics">[] }) {
  const t = useT();
  const { timeline, course } = useLectureTimeline();
  const last = timeline ? lastTaught(timeline) : null;
  const taught = course && last ? taughtTopics(course.lectures, last.id) : null;
  if (!last || !taught) return null;
  const pace = paceOf(chapters, taught);
  if (pace.taught === 0) return null;
  return (
    <section className="mb-8">
      <Card title={t("progress.pace.title")} compact>
        <p className="text-sm text-ink-700 dark:text-ink-200">
          {t("progress.pace.line", { practised: pace.practised, taught: pace.taught, id: last.id })}
        </p>
        {pace.practised < pace.taught ? (
          <Link
            to="/practice"
            className="mt-3 inline-flex h-9 items-center rounded-lg bg-accent-700 px-4 text-sm font-medium text-white hover:bg-accent-800 dark:bg-accent-600 dark:hover:bg-accent-500"
          >
            {t("progress.pace.behind")}
          </Link>
        ) : (
          <p className="mt-2 text-sm font-medium text-ink-800 dark:text-ink-100">
            {t("progress.pace.done")}
          </p>
        )}
      </Card>
    </section>
  );
}
